import * as React from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import { useCancelBooking, type Booking } from "@/lib/seating/bookings";

/**
 * Phase F-finale-7 Slice 7b.4d — Cancel-booking confirm dialog.
 *
 * Cancelling a booking frees the held/checked-in seat for the session, so it
 * is a modal confirm (not a one-click row action) and the BE requires a
 * non-empty `reason` (1..2000) recorded as `cancellation_reason`. The dialog
 * blocks submit until a reason is entered and surfaces BE errors inline
 * without closing — structurally identical to `<ReleaseAssignmentDialog>`.
 */

export interface CancelBookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking: Booking | null;
  /** Resolved labels for the confirm copy (fall back to ids). */
  seatLabel?: string;
  studentLabel?: string;
  onCancelled?: (cancelled: Booking) => void;
}

export function CancelBookingDialog({
  open,
  onOpenChange,
  booking,
  seatLabel,
  studentLabel,
  onCancelled,
}: CancelBookingDialogProps) {
  const [reason, setReason] = React.useState("");
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setReason("");
      setSubmitError(null);
    }
  }, [open]);

  const cancelMutation = useCancelBooking();
  const isSubmitting = cancelMutation.isPending;
  const trimmedReason = reason.trim();
  const canSubmit = trimmedReason.length > 0 && trimmedReason.length <= 2000 && !isSubmitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!booking || !canSubmit) return;

    try {
      const cancelled = await cancelMutation.mutateAsync({
        id: booking.id,
        body: { reason: trimmedReason },
      });
      onCancelled?.(cancelled);
      toast.success(`Cancelled booking for ${studentLabel ?? "member"}.`);
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message || "Couldn't cancel the booking. Please retry."
          : err instanceof Error
            ? err.message
            : "Couldn't cancel the booking. Please retry.";
      setSubmitError(message);
    }
  };

  const seatText = seatLabel ?? booking?.seat_id ?? "this seat";
  const studentText = studentLabel ?? booking?.student_id ?? "the member";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]" data-testid="cancel-booking-dialog">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Cancel booking</DialogTitle>
            <DialogDescription>
              This frees <span className="text-foreground font-medium">{seatText}</span> for{" "}
              <span className="text-foreground font-medium">{studentText}</span>&apos;s session and
              records why. The slot becomes available for someone else to book.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            {submitError ? (
              <div
                role="alert"
                className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
              >
                <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                <span className="flex-1">{submitError}</span>
              </div>
            ) : null}

            <div>
              <Label htmlFor="cancel-reason" className="text-xs">
                Reason<span className="text-destructive ml-0.5">*</span>
              </Label>
              <Textarea
                id="cancel-reason"
                value={reason}
                maxLength={2000}
                required
                autoFocus
                rows={3}
                className="mt-1"
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Member cancelled / no-show / double-booked"
              />
              <p className="text-muted-foreground mt-1 text-xs">
                Recorded on the booking&apos;s audit trail.
              </p>
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" type="button" disabled={isSubmitting}>
                Keep booking
              </Button>
            </DialogClose>
            <Button type="submit" variant="destructive" disabled={!canSubmit}>
              {isSubmitting ? (
                <Loader2 aria-hidden="true" className={cn("size-4 animate-spin")} />
              ) : null}
              Cancel booking
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
